import {
  type VocabularyTerm,
  type VocabularyTermInput,
  type VocabularyUpdate,
  vocabularyAddResultSchema,
  vocabularyListSchema,
  vocabularyTermSchema,
} from "@rosc/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { referenceClient } from "@/lib/orpc/client";
import { useVisitScope, type VisitScope } from "@/lib/visit-queries";

export type { VocabularyTerm };

// Under the visit-reads prefix, so the list is dropped on account change.
export const vocabularyKey = (scope: VisitScope | null) =>
  [
    "visit-reads",
    scope?.uid ?? "",
    scope?.accountPublicId ?? "",
    scope?.generation ?? 0,
    "vocabulary",
  ] as const;

export type VocabularyController = {
  terms: VocabularyTerm[];
  loading: boolean;
  failed: boolean;
  retry: () => void;
  add: (
    terms: VocabularyTermInput[],
  ) => Promise<{ added: number; skipped: number }>;
  update: (id: number, body: VocabularyUpdate) => Promise<VocabularyTerm>;
  remove: (id: number) => Promise<void>;
};

/** The signed-in clinician's saved vocabulary (SAVED_VOCABULARY_ENABLED). */
export function useVocabulary(): VocabularyController {
  const scope = useVisitScope();
  const client = useQueryClient();
  const key = vocabularyKey(scope);
  const context = { expectedUserUid: scope?.uid };
  const list = useQuery({
    queryKey: key,
    enabled: !!scope,
    retry: false,
    // Terms change only through this section, which updates the cache.
    staleTime: 60_000,
    queryFn: async ({ signal }) =>
      vocabularyListSchema.parse(
        await referenceClient.listVocabulary(
          { query: {} },
          { context, signal },
        ),
      ).items,
  });
  const add = useMutation({
    mutationFn: async (terms: VocabularyTermInput[]) =>
      vocabularyAddResultSchema.parse(
        await referenceClient.addVocabulary(
          { query: {}, body: { terms } },
          { context },
        ),
      ),
    onSuccess: (result) => client.setQueryData(key, result.items),
  });
  const update = useMutation({
    mutationFn: async ({ id, body }: { id: number; body: VocabularyUpdate }) =>
      vocabularyTermSchema.parse(
        await referenceClient.updateVocabularyTerm(
          { params: { termId: String(id) }, query: {}, body },
          { context },
        ),
      ),
    onSuccess: (term) =>
      client.setQueryData<VocabularyTerm[]>(key, (terms = []) =>
        terms.map((item) => (item.id === term.id ? term : item)),
      ),
  });
  const remove = useMutation({
    mutationFn: async (id: number) => {
      await referenceClient.deleteVocabularyTerm(
        { params: { termId: String(id) }, query: {} },
        { context },
      );
      return id;
    },
    onSuccess: (id) =>
      client.setQueryData<VocabularyTerm[]>(key, (terms = []) =>
        terms.filter((item) => item.id !== id),
      ),
    // A term already deleted elsewhere: reload the list.
    onError: () => client.invalidateQueries({ queryKey: key }),
  });
  return {
    terms: list.data ?? [],
    loading: list.isPending && list.fetchStatus !== "idle",
    failed: list.isError,
    retry: () => void list.refetch(),
    add: async (terms) => {
      const { added, skipped } = await add.mutateAsync(terms);
      return { added, skipped };
    },
    update: (id, body) => update.mutateAsync({ id, body }),
    remove: async (id) => {
      await remove.mutateAsync(id);
    },
  };
}
